import React, { useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { userAtom } from '../../store/userAtoms'
import { useTar } from '@/modules/i18n'
import { usePartnerDashboard } from '@/hooks/usePartnerDashboard'
import { useAssignments } from '@/hooks/useAssignments'
import { usePersons } from '@/hooks/usePersons'
import { useTaskDisplayNames } from '@/modules/entityLookups'
import { AssignmentEntry } from '../PartnerCohorts/Assignments/AssignmentEntry'
import { CohortPersonDetails } from '../PartnerCohorts/cohortPageData'
import { UserService } from '@duck/api-definitions'
import { Card, CardContent, Skeleton } from '@duck/ui'
import { NoDataEmptyState } from '../EmptyStates'
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { usePartnerTar } from '@/hooks/usePartnerTar'

export const PartnerDashboard: React.FC = () => {
  const user = useAtomValue(userAtom)
  const { t } = useTar()
  const { t: tP } = usePartnerTar()
  const { recentAssignments, isLoading: isLoadingDashboard, refetch: refetchDashboard } = usePartnerDashboard()

  // Extract unique assignment IDs from recent assignments
  const assignmentIds = useMemo(() => {
    return Array.from(new Set(recentAssignments.map(ad => ad.assignmentId)))
  }, [recentAssignments])

  // Fetch full assignment objects
  const { assignments, isLoading: isLoadingAssignments, refetch: refetchAssignments } = useAssignments(assignmentIds)

  // Extract all person IDs from recent assignments
  const allPersonIds = useMemo(() => {
    return Array.from(new Set(recentAssignments.map(ad => ad.personId)))
  }, [recentAssignments])

  // Fetch person data
  const { persons, isLoading: isLoadingPersons } = usePersons(allPersonIds)

  // Get task IDs for display names
  const taskIds = useMemo(() => {
    return Array.from(new Set(recentAssignments.map(ad => ad.taskId)))
  }, [recentAssignments])

  const { getDisplayName, isLoading: isTaskLoading } = useTaskDisplayNames(taskIds, {
    fallback: t('common.na')
  })

  // Group assignmentsDetails by assignmentId and create CohortPersonDetails
  const assignmentsByAssignmentId = useMemo(() => {
    if (!recentAssignments || recentAssignments.length === 0) {
      return {}
    }

    const personsMap = persons.reduce((acc, person) => {
      acc[person.id] = person
      return acc
    }, {} as Record<string, UserService.Person>)

    const result: Record<string, CohortPersonDetails[]> = {}

    recentAssignments.forEach(assignmentDetail => {
      const assignmentId = assignmentDetail.assignmentId
      if (!result[assignmentId]) {
        result[assignmentId] = []
      }

      const person = personsMap[assignmentDetail.personId]
      if (person) {
        result[assignmentId].push({
          personId: assignmentDetail.personId,
          email: person.email,
          onboardingStatus: person.onboardingStatus,
          createdAt: person.createdAt,
          assignmentStatus: assignmentDetail.status,
          grade: assignmentDetail.grade,
          feedback: assignmentDetail.feedback,
          solution: assignmentDetail.solution,
          startDate: assignmentDetail.startDate,
          submitDate: assignmentDetail.submitDate,
          workTime: assignmentDetail.workTime,
        })
      }
    })

    return result
  }, [recentAssignments, persons])

  const isLoading = isLoadingDashboard || isLoadingAssignments || isLoadingPersons

  const handleAssignmentChange = () => {
    refetchAssignments()
    refetchDashboard()
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          {t('partner.dashboard')}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('partner.dashboardDescription', {
            firstName: user?.firstName || '',
            lastName: user?.lastName || ''
          })}
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-32 w-full" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-32 w-full" />
              </div>
            </CardContent>
          </Card>
        </div>
      ) : assignmentIds.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <NoDataEmptyState
              icon={ClipboardDocumentListIcon}
              title={tP('noAssignmentsYet')}
              description={tP('noAssignmentsDescription')}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-4">
              {t('partner.recentAssignments')}
            </h2>
            <div className="space-y-6">
              {assignmentIds.map(assignmentId => {
                const assignment = assignments[assignmentId]
                const peopleDetails = assignmentsByAssignmentId[assignmentId] || []
                const personIds = peopleDetails.map(pd => pd.personId)
                const assignmentPersons = persons.filter(p => personIds.includes(p.id))

                if (!assignment) {
                  return null
                }

                return (
                  <AssignmentEntry
                    key={assignmentId}
                    assignment={assignment}
                    peopleDetails={peopleDetails}
                    assignmentPersons={assignmentPersons}
                    getDisplayName={getDisplayName}
                    isTaskLoading={isTaskLoading}
                    onAssignmentChange={handleAssignmentChange}
                  />
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
